import React, { useState, useRef, useEffect } from "react";
import "../../assets/scss/pages/weddingInvitation/onboarding.scss";
import BottomFloatingButton from "@src/components/weddingHall/BottomFloatingButton";
import { hasWeddingInvitation } from "@src/api/weddingInvitation/weddingInvitation";
import { HOSTNAME, isExternalUrl } from "@src/utils";
import AppHeader from "@src/components/common/appHeader/AppHeader";
import kakaoPixel from "@src/utils/kakaoPixel";
import { trackCustomEvent, trackPageView } from "@src/utils/fbq";
import { logEvent } from "@src/reactGa";

interface TextContentProps {
  title: string;
  description: string;
}

interface FeatureProps extends TextContentProps {
  image?: string;
  alt?: string;
}

const FEATURES = [
  {
    title: "내 취향대로 고를 수 있는<br/>다양한 템플릿",
    description:
      "전문 디자이너가 만든 템플릿과 감성적인 인사말 샘플로, 누구나 손쉽게 높은 퀄리티의 청첩장을 완성할 수 있어요.",
    image: require("../../assets/images/weddingInvitation/onboarding/img_onboarding01@3x.png"),
    alt: "다양한 템플릿",
  },
  {
    title: "평생 간직할 수 있는 무료 청첩장",
    description:
      "초대받는 사람마다 전하고 싶은 마음이 다르니까, 신랑 친구, 신부 친구, 부모님 하객별로 각자의 이야기를 담은 청첩장을 자유롭게 만들어보세요. 몇 장이든, 모두 무료로 영원히 보관됩니다.",
    image: require("../../assets/images/weddingInvitation/onboarding/img_onboarding02@3x.png"),
    alt: "무료 청첩장",
  },
  {
    title: "마음이 선물이 되는 순간<br/>함께 만드는 웨딩 펀딩",
    description:
      "축의금 대신, 신혼여행·예식 준비·신혼살림까지 현금 펀딩과 선물 펀딩으로 두 사람의 시작을 함께 채워보세요.",
    image: require("../../assets/images/weddingInvitation/onboarding/img_onboarding03@3x.png"),
    alt: "웨딩 펀딩",
  },
  {
    title: "하객 관리부터 감사 인사까지<br/>청첩장 하나로 스마트하게 끝!",
    description:
      "참석 여부, 방명록, 선물, 펀딩 현황까지 한눈에 관리하고 만든 청첩장도 언제든 자유롭게 수정할 수 있어요. 결혼식 전후의 모든 순간을 웨딩북에서 완성하세요.",
    image: require("../../assets/images/weddingInvitation/onboarding/img_onboarding04@3x.png"),
    alt: "하객 관리",
  },
];

const OnboardingPage: React.FC = () => {
  const [currentSection, setCurrentSection] = useState(0);
  const [buttonText, setButtonText] = useState("아래로 내리기");

  const headlineRef = useRef<HTMLDivElement>(null);
  const featureRefs = useRef<(HTMLDivElement | null)[]>([]);
  const comingSoonRef = useRef<HTMLDivElement>(null);

  const totalSections = FEATURES.length + 2;

  useEffect(() => {
    kakaoPixel?.pageView();
    kakaoPixel?.viewContent();

    trackPageView();
    trackCustomEvent("InvitationMakeStart");
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      const isAtBottom = scrollTop + windowHeight >= documentHeight - 10;

      if (isAtBottom && currentSection < totalSections - 1) {
        setCurrentSection(totalSections - 1);
        setButtonText("지금 청첩장 만들어보기");
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [currentSection, totalSections]);

  const scrollToSection = (sectionIndex: number) => {
    if (sectionIndex === 0 && headlineRef.current) {
      headlineRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else if (sectionIndex > 0 && sectionIndex <= FEATURES.length) {
      const featureIndex = sectionIndex - 1;
      const featureRef = featureRefs.current[featureIndex];
      if (featureRef) {
        featureRef.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    } else if (sectionIndex === totalSections - 1 && comingSoonRef.current) {
      comingSoonRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  /* 버튼 클릭 시 아래로 스크롤 또는 청첩장 메인으로 이동 */
  const handleButtonClick = () => {
    const isBeforeLast = currentSection < totalSections - 1;
    const nextSection = currentSection + 1;

    if (isBeforeLast) {
      if (nextSection === totalSections - 2) {
        setCurrentSection(nextSection);
        setButtonText("지금 청첩장 만들어보기");
        scrollToSection(nextSection);
        return;
      }

      if (nextSection < totalSections - 2) {
        setCurrentSection(nextSection);
        setButtonText("아래로 내리기");
        scrollToSection(nextSection);
        return;
      }
    }

    logEvent("landing_move_write", {
      behavior: "click",
      content: "onboarding_click_button",
      name: "onboarding_click_button",
      transport: "beacon",
    });
    window.location.href = isExternalUrl(`${HOSTNAME}/wedding-invitation/write?from=intro`);
  };

  /* 이미 생성된 청첩장이 있는 유저의 경우 청첩장 메인으로 이동 */
  // useEffect(() => {
  //   hasWeddingInvitation().then((exists) => {
  //     if (exists) {
  //       window.location.assign('h2mwbell://invitation/main');
  //     }
  //   });
  // }, []);

  const closeWebview = () => {
    window.location.href = "h2mwbell://webview/close";
  };

  return (
    <div className="onboarding-page">
      <AppHeader
        type="nonStack"
        actionItems={[
          {
            name: "close",
            callback: closeWebview,
          },
        ]}
      />
      <div className="main-animation">
        <img
          src={require("../../assets/images/weddingInvitation/onboarding/랜딩메인_re.png")}
          alt="웨딩북 모바일 청첩장"
          className="main-video"
        />
      </div>

      <div ref={headlineRef}>
        <Headline
          title="가장 나다운 결혼의 시작,<br/>우리의 이야기를 담은 청첩장"
          description="세상 하나뿐인 우리의 순간을 '나답게' 표현하세요."
        />
      </div>

      {FEATURES.map((feature, index) => (
        <div key={index} ref={(el) => (featureRefs.current[index] = el)}>
          <Feature
            title={feature.title}
            description={feature.description}
            image={feature.image}
            alt={feature.alt}
          />
        </div>
      ))}

      <div ref={comingSoonRef}>
        <p className="small">다양한 기능이 계속 업데이트될 예정입니다.</p>
        <div className="coming-soon">
          <img
            src={require("../../assets/images/weddingInvitation/onboarding/--coming soon--.png")}
            alt="coming soon"
          />
        </div>
      </div>

      <div className="bottom-floating-button">
        <BottomFloatingButton
          partnerId=""
          buttonName={buttonText}
          onClose={() => null}
          handleRightBtnClick={handleButtonClick}
          isDuplicateBooking={false}
          setIsDuplicateBooking={() => {}}
        />
      </div>
    </div>
  );
};

const Headline: React.FC<TextContentProps> = ({ title, description }) => (
  <div className="headline">
    <p className="tag">NEW</p>
    <h2 dangerouslySetInnerHTML={{ __html: title }} />
    <p>{description}</p>
    <div className="scroll-indicator">
      <div className="chevron"></div>
      <div className="chevron"></div>
    </div>
  </div>
);

const Feature: React.FC<FeatureProps> = ({ title, description, image, alt }) => (
  <div className="feature">
    <h3 dangerouslySetInnerHTML={{ __html: title }} />
    <p>{description}</p>
    {image && <img src={image} alt={alt} />}
  </div>
);

export default OnboardingPage;
